<script lang="ts">
  import { ProfilePicture, SVG } from "$lib/components";
  import { authStore as Current } from "$lib/stores";
  import { simpleHash, cn } from "$lib/utility";
  import { routes } from "$lib/routes";
  import { SUBSCRIPTION_TIER } from "$lib/types";
  import Tooltip from "$lib/components/basic/tooltip/tooltip.svelte";
  import { Sidebar } from "$lib/components/ui";
  import { goto } from "$app/navigation";
  import { page } from "$app/state";

  import { signInIcon } from "$lib/images/icons";
  import { isDevelopment } from "$lib/utility/environment";

  let {
    isCollapsed,
    borderColor,
    sidebar,
    handleSidebarNavigation,
  }: {
    isCollapsed: boolean;
    borderColor: string;
    sidebar: any;
    handleSidebarNavigation: () => void;
  } = $props();

  const isSettingsPage = $derived(page.url.pathname.startsWith("/settings"));

  function navigateToSettings() {
    handleSidebarNavigation();
    goto("/settings");
  }
</script>

<div
  class={cn(
    "sidebar-menuitem transition-default h-18 dark:border-theme-background-secondary relative flex w-full flex-shrink-0 items-center justify-start gap-1 border-t border-black",
    isCollapsed ? "justify-center" : "px-5",
    sidebar.isMobile ? `border-b ${borderColor}` : "",
    isSettingsPage && !sidebar.isMobile ? "dark:bg-theme-green bg-black text-white dark:text-black" : "",
  )}
>
  <div class={cn("", isCollapsed ? "h-5 w-5" : "h-8 w-8")}>
    <Tooltip content="User menu" side="right">
      <button
        type="button"
        onclick={() => navigateToSettings()}
        data-testid="user-profile-button"
        class="cursor-pointer"
      >
        <ProfilePicture
          wrapperClass={cn("flex-shrink-0 transition-default", isCollapsed ? "h-5 w-5 p-[1px]" : "h-8 w-8")}
          imageUrl={`https://www.gravatar.com/avatar/${simpleHash($Current.user?.email || "default")}?d=identicon&s=64`}
          fallbackImageUrl="https://www.gravatar.com/avatar/default?d=identicon&s=64"
          shape="circle"
          outline={true}
          inverted={isSettingsPage}
          altText="Profile"
        />
      </button>
    </Tooltip>
    {#if Current.isAdmin()}
      <span
        class={cn(
          "transition-default rounded-0 0 tracking-narrow absolute left-2 right-2 top-0 py-0 text-center text-[8px] font-medium",
          "bg-gray-200b border-b-1 border-l-1 border-r-1 text-theme-text-primary dark:border-theme-background-tertiary dark:bg-theme-green border-t-0 border-black dark:text-black",
        )}
      >
        Admin
      </span>
    {:else if isDevelopment()}
      <span
        class={cn(
          "transition-default rounded-0 0 tracking-narrow absolute left-2 right-2 top-0 py-0 text-center text-[8px] font-medium",
          "bg-gray-200b border-b-1 border-l-1 border-r-1 text-theme-text-primary dark:border-theme-background-tertiary dark:bg-theme-green border-t-0 border-black dark:text-black",
        )}
      >
        Dev
      </span>
    {/if}
    {#if $Current.user?.plan === SUBSCRIPTION_TIER.PLUS || $Current.user?.plan === SUBSCRIPTION_TIER.PRO || $Current.user?.plan === SUBSCRIPTION_TIER.TEAM}
      <span
        class={cn(
          sidebar.open
            ? "transition-default text-2xs absolute -right-2 -top-2.5 rounded-full border border-white px-[5px] py-[1px] text-black"
            : "hidden",
          $Current.user?.plan === SUBSCRIPTION_TIER.PRO
            ? "border border-white bg-black text-white"
            : $Current.user?.plan === SUBSCRIPTION_TIER.TEAM
              ? "border border-white bg-black px-0 text-white"
              : "bg-theme-green",
        )}
      >
        {#if $Current.user?.plan === SUBSCRIPTION_TIER.PLUS}Plus{/if}
        {#if $Current.user?.plan === SUBSCRIPTION_TIER.PRO}Pro{/if}
        {#if $Current.user?.plan === (SUBSCRIPTION_TIER.TEAM as string)}Team{/if}
      </span>
    {/if}

    {#if !sidebar.open && !sidebar.isMobile && $Current.user?.plan !== SUBSCRIPTION_TIER.FREE}
      <span
        class={cn(
          "transition-default rounded-0 rounded-t-base absolute bottom-0 left-2 right-2 border border-black px-[5px] py-0 text-center text-[9px] font-medium tracking-wide text-black",
          $Current.user?.plan === SUBSCRIPTION_TIER.PRO
            ? "bg-theme-text-primary text-theme-background-primary border "
            : $Current.user?.plan === SUBSCRIPTION_TIER.TEAM
              ? "bg-theme-text-primary text-theme-background-primary border px-0"
              : "bg-theme-green dark:bg-black dark:text-white",
          isSettingsPage ? "bg-theme-green text-black" : "",
        )}
      >
        {#if $Current.user?.plan === SUBSCRIPTION_TIER.PLUS}Plus{/if}
        {#if $Current.user?.plan === SUBSCRIPTION_TIER.PRO}Pro{/if}
        {#if $Current.user?.plan === (SUBSCRIPTION_TIER.TEAM as string)}Team{/if}
      </span>
    {/if}
  </div>
  <Sidebar.MenuButton
    class={cn(
      "text-theme-text-primary -mt-1 w-full bg-transparent font-light transition-all duration-300 ease-in-out hover:bg-transparent hover:font-semibold",
      isCollapsed ? "hidden" : "inline-block",
    )}
    onclick={(e: Event) => {
      e.stopPropagation();
      goto("/settings");
      handleSidebarNavigation();
    }}
  >
    <div class="flex cursor-pointer items-center gap-3 overflow-ellipsis text-sm">
      {$Current.user?.email || "?"}
    </div>
  </Sidebar.MenuButton>
</div>
